/* eslint-disable react/no-array-index-key */
import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import cx from 'classnames';

import Label from 'components/Label';
import Microdata from 'components/Microdata';
import NextLink from 'components/NextLink';
import Place from 'components/Place';
import ShowOnlyClient from 'components/ShowOnlyClient';
import Thumbnail from 'components/Thumbnail';

import { ThumbnailFileCropType } from 'utils/propTypes/common';

import styles from './styles.module.scss';

const ariaDateFormatter = new Intl.DateTimeFormat('ru-RU', {
	month: 'long',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
	weekday: 'long'
});

const EntityCard = ({
	_id,
	title,
	place,
	labels,
	image,
	size,
	orientation,
	date,
	url,
	active,
	theme,
	themePlace,
	onClick,
	gutter,
	markupAttributes,
	subtitleItems,
	microdata,
	imageCrop,
	ariaHidden = true
}) => {
	const entityCardCx = cx(
		styles.card,
		styles[`theme_${theme}`],
		styles[`size_${size}`],
		styles[`orientation_${orientation}`],
		styles[`gutter_${gutter}`],
		{
			[styles.active]: active,
			[styles[`theme_${themePlace}Place`]]: !!(themePlace && place)
		}
	);
	const onCardClick = useCallback((event) => {
		if (event.target.tagName !== 'A' && onClick) {
			onClick(_id);
		}
	}, []);

	const onKeyDown = useCallback((event) => {
		const keys = ['Enter', 'Space', ' '];
		if (keys.includes(event.key)) {
			event.preventDefault();
			onClick(_id);
		}
	}, []);

	const hasCropParams = !_.isEmpty(imageCrop);
	let imageParams;

	if (hasCropParams) {
		imageParams = { crop: 'crop', gravity: 'center', ...imageCrop };
	} else {
		const imageSizeParamsHash = {
			portrait: {
				width: 211,
				height: 282
			},
			landscape: {
				width: 324,
				height: 200
			}
		};

		imageParams = imageSizeParamsHash[orientation];
	}

	const backgroundParams = useMemo(
		() => (url ? { href: url } : { onClick: onCardClick, onKeyDown }),
		[url, onClick]
	);

	const subtitleFiltered =
		subtitleItems && subtitleItems.filter((iterator) => !!iterator);

	const subtitleContent =
		subtitleFiltered && subtitleFiltered.length ? (
			<div className={styles.subtitle}>
				{subtitleFiltered.map((item, index) => (
					<span className={styles.subtitleItem} aria-label={item} key={index}>
						{item}
					</span>
				))}
			</div>
		) : null;

	const labelContent =
		!_(labels).isEmpty() &&
		labels.map((label, index) => (
			<div key={`label-${index}`} className={styles.headElement}>
				<Label content={label} size="small" />
			</div>
		));

	const content = (
		<>
			<ShowOnlyClient>
				<div aria-hidden="true" className={styles.head}>
					{date && (
						<div className={styles.headBlock}>
							<div className={styles.headElement}>
								<Label
									type="date"
									content={format(date, 'dd MMM', { locale: ruLocale }).replace(
										'.',
										''
									)}
								/>
							</div>
						</div>
					)}
					{(date || labelContent) && (
						<div className={cx(styles.headBlock, styles.secondary)}>
							{date && (
								<div className={styles.headElement}>
									<Label
										type="date"
										content={format(date, 'EEEEEE HH:mm', { locale: ruLocale })}
									/>
								</div>
							)}
							{labelContent}
						</div>
					)}
				</div>
			</ShowOnlyClient>
			<div className={styles.card_screenreaderText}>
				<div>{title}</div>
				{date && <div>{ariaDateFormatter.format(new Date(date))}</div>}
				{place?.title && <div>{place?.title}</div>}
				{subtitleFiltered?.length &&
					subtitleFiltered.map((item, index) => <div key={index}>{item}</div>)}
			</div>
			<div className={styles.body} aria-hidden="true">
				<span className={styles.title}>{title}</span>
				{subtitleContent ? <span>{subtitleContent}</span> : null}
				{place && themePlace !== 'hide' && (
					<div className={styles.place} aria-hidden>
						<Place
							title={place.title}
							url={place.url}
							tabIndex={-1}
							ariaHidden
						/>
					</div>
				)}
			</div>
		</>
	);

	return (
		<div className={entityCardCx} {...markupAttributes} {...backgroundParams}>
			<Thumbnail
				file={image || {}}
				className={styles.image}
				params={imageParams}
				keepRatio
				ariaHidden={ariaHidden}
			/>
			<div className={styles.content}>
				{url ? (
					<NextLink href={url} tabIndex={0} className={styles.contentInner}>
						{content}
					</NextLink>
				) : (
					<div tabIndex={0} role="button" className={styles.contentInner}>
						{content}
					</div>
				)}
				{place && themePlace === 'hide' && (
					<div className={styles.place} aria-hidden="true">
						<Place
							title={place.title}
							url={place.url}
							tabIndex={-1}
							ariaHidden
						/>
					</div>
				)}
			</div>
			{theme === 'overlay' && <div className={styles.overlay} />}
			{microdata && <Microdata>{microdata}</Microdata>}
		</div>
	);
};

EntityCard.propTypes = {
	_id: PropTypes.number,
	title: PropTypes.string.isRequired,
	orientation: PropTypes.oneOf(['portrait', 'landscape']),
	image: PropTypes.object,
	place: PropTypes.shape({
		title: PropTypes.string.isRequired,
		url: PropTypes.string
	}),
	labels: PropTypes.arrayOf(PropTypes.node),
	size: PropTypes.oneOf(['elastic']),
	date: PropTypes.object,
	url: PropTypes.string,
	active: PropTypes.bool,
	theme: PropTypes.oneOf(['overlay']),
	themePlace: PropTypes.oneOf(['hide']),
	gutter: PropTypes.oneOf(['small']),
	onClick: PropTypes.func,
	markupAttributes: PropTypes.object,
	subtitleItems: PropTypes.arrayOf(PropTypes.node),
	microdata: PropTypes.node,
	imageCrop: ThumbnailFileCropType,
	ariaHidden: PropTypes.bool
};

EntityCard.defaultProps = {
	active: false,
	theme: 'overlay',
	orientation: 'landscape'
};

export default EntityCard;
